import React from 'react';
import {connect} from 'react-redux';
import {defineMessages, FormattedMessage} from 'react-intl';

import {Map, List} from 'immutable';

import * as BetTypeMap from 'constants/BetType';
import * as AcceptFactorChangeMode from 'constants/AcceptFactorChange';

import {makeBet as makeBetAction} from 'actions/creators/MakeBet';
import {setCouponAmount as setCouponAmountAction,
    acceptRateValueChange as acceptRateValueChangeAction} from 'actions/creators/CouponRate';
import {balance as getBalanceAction} from 'actions/balance/balanceAction';
import {requestBetTypes as requestBetTypesAction} from 'actions/creators/HistoryBet';
import {toggleFreebet as toggleFreebetAction} from 'actions/creators/Freebet';

import {isRateUnavailable, rateFactorChangeConfirmNeeded} from './rateUtils';

import BetType from './BetType.jsx';
import FavouriteBetAmount from './FavouriteBetAmount.jsx';

import {minAmount as MinAmountValues} from 'constants/StakeMinAmount';
import {maxWinAmount as MaxWinAmountValues} from 'constants/StakeMaxWinAmount';

import {resourceNames} from 'constants/accountResourceNames';

import { STAKES_VALUES } from '../CouponDropDownMenu.jsx';

const messages = defineMessages({
    makeBet: {
        id: 'coupon.make_bet',
        defaultMessage: 'Сделать ставку'
    },
    confirmRateValueChange: {
        id: 'coupon.confirm_rate_value_change',
        defaultMessage: 'Принять'
    },
    totalOdd: {
        id: 'coupon.total_odd_hint',
        defaultMessage: 'Общий коэффициент'
    },
    possibleWinning: {
        id: 'coupon.possible_winning_hint',
        defaultMessage: 'Возможный выигрыш'
    },
    stake: {
        id: 'coupon.stake_hint',
        defaultMessage: 'Сумма ставки'
    },
    minAmountHint: {
        id: 'coupon.min_amount_hint',
        defaultMessage: 'Мин.'
    },
    maxAmountHint: {
        id: 'coupon.max_amount_hint',
        defaultMessage: 'Макс. {amount} {resource}'
    }
});

class Controls extends React.Component {
    static propTypes = {
        languageId: React.PropTypes.number.isRequired,
        user: React.PropTypes.object,
        betSlips: React.PropTypes.object,
        currentBetTypeIndex: React.PropTypes.number.isRequired,
        betTypes: React.PropTypes.object,
        amount: React.PropTypes.number,
        minBetAmount: React.PropTypes.number,
        maxWinAmount: React.PropTypes.number,
        isFreebet: React.PropTypes.bool,
        freebetAvailable: React.PropTypes.bool,
        freebet: React.PropTypes.object,
        bettingMode: React.PropTypes.object,
        nextKey: React.PropTypes.number,
        bettingAvailable: React.PropTypes.bool.isRequired,
        makeBetAction: React.PropTypes.func.isRequired,
        setCouponAmountAction: React.PropTypes.func.isRequired,
        getBalanceAction: React.PropTypes.func.isRequired,
        acceptRateValueChangeAction: React.PropTypes.func.isRequired,
        requestBetTypesAction: React.PropTypes.func.isRequired,
        toggleFreebetAction: React.PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);

        const amount = props.amount || 0;
        const inActive = (amount < props.minBetAmount);

        this.state = {inActive, rawAmount: amount};

        this._onMakeBetClick = this._onMakeBetClick.bind(this);
        this._onAmountChange = this._onAmountChange.bind(this);
        this._onFavouriteBetAmountClick = this._onFavouriteBetAmountClick.bind(this);
        this._onToggleFreebetClick = this._onToggleFreebetClick.bind(this);
        this._onConfirmClick = this._onConfirmClick.bind(this);
    }

    shouldComponentUpdate(nextProps, nextState) {
        return nextProps.languageId !== this.props.languageId ||
            nextProps.betSlips !== this.props.betSlips ||
            nextProps.amount !== this.props.amount ||
            nextProps.isFreebet !== this.props.isFreebet ||
            nextProps.currentBetTypeIndex !== this.props.currentBetTypeIndex ||
            nextProps.betTypes !== this.props.betTypes ||
            nextProps.bettingMode !== this.props.bettingMode ||
            nextProps.nextKey !== this.props.nextKey ||
            nextState.rawAmount !== this.state.rawAmount;
    }

    componentWillUpdate = (nextProps) => {
        if(nextProps.alternativeStake && this.state.rawAmount > 0 && nextProps.amount != this.state.rawAmount){
            this.setState({rawAmount: nextProps.amount})
        }
    }

    _onMakeBetClick(e) {
        e.preventDefault();

        const {amount} = this.props;
        if (amount <= 0) {
            return;
        }

        const {currentBetTypeIndex, betTypes, isFreebet} = this.props;
        if (currentBetTypeIndex === -1) {
            return;
        }

        const betType = betTypes.get(currentBetTypeIndex);

        const betSlips = this.props.betSlips;
        const bettingMode = this.props.bettingMode;
        const acceptFactorChangeMode = bettingMode.get('acceptFactorChange');

        let makeBetPossible = true;
        betSlips.forEach((bs) => {
            const rate = bs.get('rate');

            if (rateFactorChangeConfirmNeeded(rate, acceptFactorChangeMode) || isRateUnavailable(rate)) {
                makeBetPossible = false;
                return false;
            }
        });

        if (makeBetPossible === false) {
            return;
        }

        let allowIncrease = false;
        let allowDecrease = false;

        if (acceptFactorChangeMode === AcceptFactorChangeMode.INCREASE) {
            allowIncrease = true;
        } else if (acceptFactorChangeMode === AcceptFactorChangeMode.DECREASE) {
            allowDecrease = true;
        } else if (acceptFactorChangeMode === AcceptFactorChangeMode.BOTH) {
            allowIncrease = true;
            allowDecrease = true;
        }

        const betTypeId = betType.type;
        const betTypeRank = betType.rank;

        let betId = this.props.nextKey;

        const bets = [];
        const historyBets = [];

        if (betTypeId === BetTypeMap.SINGLE) {
            betSlips.valueSeq().forEach(betSlip => {
                const id = (betId++).toString();
                const rate = betSlip.get('rate');
                const factorValue = rate.get('value');
                const factorId = rate.get('id');

                const transformedMap = new List().push(Controls.transformBetSlip(betSlip));

                bets.push({
                    id,
                    type: betTypeId,
                    rank: betTypeRank,
                    amount,
                    factorValue,
                    factors: transformedMap,
                    oneClick: false,
                    allowIncrease,
                    allowDecrease,
                    isFreebet
                });

                const betSlipMap = new Map().set(factorId, betSlip);

                historyBets.push({
                    id,
                    betType: betTypeId,
                    rank: betTypeRank,
                    amount,
                    odd: factorValue,
                    betSlips: betSlipMap
                });
            });
        } else {
            const id = betId.toString();

            bets.push({
                id,
                type: betTypeId,
                rank: betTypeRank,
                amount,
                factorValue: betType.odd,
                factors: Controls.transform(betSlips),
                oneClick: false,
                allowIncrease,
                allowDecrease,
                isFreebet
            });

            historyBets.push({
                id,
                betType: betTypeId,
                rank: betTypeRank,
                amount,
                odd: betType.odd,
                betSlips
            });
        }

        if(this.props.user.get('isVerified') === false) {
            window.toggleVerifyModal && window.toggleVerifyModal(true)
        } else {
            this.props.makeBetAction(bets, historyBets);
        }
        this.props.getBalanceAction({accountId: this.props.user.get('accountId')});
    }

    static transform(rates) {
        let transformedRates = new List();

        rates.valueSeq().forEach(rate => {
            transformedRates = transformedRates.push(Controls.transformBetSlip(rate));
        });

        return transformedRates;
    }

    static transformBetSlip(betSlip) {
        const rate = betSlip.get('rate');

        return new Map({
            eventId: rate.get('matchId'),
            eventTimeAndZone: betSlip.getIn(['match', 'timeAndZone']),
            outcomeId: rate.get('outcomeId'),
            factorId: rate.get('id'),
            factorValue: rate.get('value'),
            isConditional: false
        });
    }

    _onFavouriteBetAmountClick(amount) {
        this.props.toggleFreebetAction(false);
        this.props.setCouponAmountAction(amount);
        this.setState({inActive: false, rawAmount: amount});
    }

    _onAmountChange(e) {
        e.preventDefault();

        const {minBetAmount} = this.props;
        let rawAmount = e.target.value;

        // checking for length
        if (!rawAmount.length) {
            this.setState({inActive: true, rawAmount: ''});
            this.props.setCouponAmountAction(0);
        }

        const amount = Number(rawAmount);

        if (isNaN(amount)) {
            return ;
        }

        const splittedRawAmount = rawAmount.split('.');
        const zeroSum = (splittedRawAmount[0].length > 1 && amount == 0.0);
        const longDecimalPart = (splittedRawAmount[1] !== undefined && splittedRawAmount[1].length > 2);

        // checking for several zeros and long decimal part of value
        if (zeroSum || longDecimalPart) {
            return ;
        }

        // concating splitted value to normal amount
        rawAmount = Number(splittedRawAmount[0]).toString();
        if (splittedRawAmount[1] !== undefined) {
            rawAmount = rawAmount + '.' + splittedRawAmount[1];
        }

        const splittedAmount = amount.toString().split('.');
        const ceilPart = splittedAmount[0];
        const decimalPart = splittedAmount[1] !== undefined ? splittedAmount[1] : '';

        // checking for fract.num length
        if ((ceilPart.toString().length > 7) || (decimalPart.toString().length > 2)) {
            return ;
        }

        this.setState({inActive: (amount < minBetAmount), rawAmount});
        this.props.setCouponAmountAction(amount);
    }

    _onToggleFreebetClick(e) {
        e.preventDefault();

        const {freebet, isFreebet} = this.props;

        if (isFreebet) {
            this.props.setCouponAmountAction(0);
            this.props.toggleFreebetAction(false);
            this.setState({inActive: true, rawAmount: 0});
        } else {
            const freebetAmount = freebet.get('amount');
            this.props.setCouponAmountAction(freebetAmount);
            this.props.toggleFreebetAction(true);
            this.setState({inActive: false, rawAmount: freebetAmount});
        }
    }

    _onConfirmClick(changedRates, e) {
        e.preventDefault();

        this.props.acceptRateValueChangeAction(changedRates);
        this.props.requestBetTypesAction();
    }

    render() {
        const {inActive, rawAmount} = this.state;
        const {bettingMode, betSlips, betTypes, currentBetTypeIndex, bettingAvailable,
                isFreebet, freebetAvailable, amount, alternativeStake, minBetAmount, maxWinAmount, resourceName, countryCode} = this.props;
        const currentBetType = betTypes.get(currentBetTypeIndex);
        const betType = betTypes.get(currentBetTypeIndex);
        const currentBetTypeIsSingle = betType && betType.type == BetTypeMap.SINGLE;

        const odd = currentBetType ? currentBetType.odd : 0;

        let possibleWinAmount = 0;

        if (amount && !isNaN(amount)) {
            possibleWinAmount = (amount * odd).toFixed(2);
        }

        if (betType && betType.type == BetTypeMap.SYSTEM && betType.rank !== undefined) {
            possibleWinAmount = (amount * odd).toFixed(2);
        }

        const maxRestriction = possibleWinAmount > maxWinAmount;

        const values = STAKES_VALUES[countryCode]
        const favouriteBet1Amount = bettingMode.get('favouriteBet1Amount') || values[1];
        const favouriteBet2Amount = bettingMode.get('favouriteBet2Amount') || values[2];
        const favouriteBet3Amount = bettingMode.get('favouriteBet3Amount') || values[3];

        let makeBetEnabled = !inActive && !maxRestriction;
        let confirmRateChangeSpan;
        const changedFactorRates = [];

        betSlips.forEach((r) => {
            const rate = r.get('rate');
            const id = rate.get('id');
            const actualValue = rate.get('actualValue');

            if (rateFactorChangeConfirmNeeded(rate)) {
                changedFactorRates.push({id, actualValue});
            }

            if (makeBetEnabled && isRateUnavailable(rate)) {
                makeBetEnabled = false;
            }
        });

        if (changedFactorRates.length > 0) {
            makeBetEnabled = false;
            const boundConfirmClick = this._onConfirmClick.bind(this, changedFactorRates);

            confirmRateChangeSpan = (
                <div className="btn__accept-changes" onClick={boundConfirmClick}>
                    <a href="#"
                       className="message-box--sell-bet__btn--confirm"
                       onClick={boundConfirmClick}>
                        <FormattedMessage {...messages.confirmRateValueChange} />
                    </a>
                </div>);
        }

        let cls = 'coupon--make-bet make-bet btn__orange make-bet__btn';
        if (!makeBetEnabled || !bettingAvailable) {
            cls += ' btn__disabled';
        }

        return (
            <div className="coupon-controls">
                <BetType/>
                <FormattedMessage {...messages.totalOdd}>
                    {(message) =>
                        <div className="rate" title={message}>
                            <span>{odd.toFixed(2)}</span>
                        </div>
                    }
                </FormattedMessage>
                <FormattedMessage {...messages.possibleWinning}>
                    {(message) =>
                        <div className="balance" title={message}>
                            <span>{possibleWinAmount}</span>
                        </div>
                    }
                </FormattedMessage>
                {maxRestriction && <FormattedMessage {...messages.maxAmountHint }
                                                     values={{amount: maxWinAmount, resource: resourceName}}>
                    {(message) => <p className="max-win-amount-hint">{message}</p>}
                </FormattedMessage>}
                <div className="bet-amount cf">
                    <input ref="betAmount" value={rawAmount} type="text"
                           readOnly={isFreebet} id="bet-amount" className="bet"
                           onChange={this._onAmountChange} onBlur={this._onAmountChange}/>
                    {inActive && <FormattedMessage {...messages.minAmountHint}>
                        {(message) => <p className="min-bet-amount-hint">{message} {minBetAmount}</p>}
                    </FormattedMessage>}
                    {!inActive && !maxRestriction && currentBetTypeIsSingle && betSlips.size > 1 &&
                    <p className="total-rates"> X {betSlips.size} = <strong>{amount * betSlips.size}</strong></p>}
                    {freebetAvailable && <div className="freebet--icon freebet--icon__active freebet--icon__bet-amount"
                                              onClick={this._onToggleFreebetClick}/>}
                </div>
                    {alternativeStake && <div className="bet-error-message"><FormattedMessage {...messages.maxAmountHint }
                    values={{amount: alternativeStake, resource: resourceName}}/></div>}
                <div className="favourites-bet-amounts cf">
                    <FavouriteBetAmount number={1}
                                        amount={favouriteBet1Amount}
                                        callback={this._onFavouriteBetAmountClick}/>
                    <FavouriteBetAmount number={2}
                                        amount={favouriteBet2Amount}
                                        callback={this._onFavouriteBetAmountClick}/>
                    <FavouriteBetAmount number={3}
                                        amount={favouriteBet3Amount}
                                        callback={this._onFavouriteBetAmountClick}/>
                </div>

                {confirmRateChangeSpan}

                <div className={cls}>
                    {freebetAvailable && isFreebet &&
                    <div className="freebet--icon freebet--icon__active freebet--icon__make-bet"/>}

                    <FormattedMessage {...messages.makeBet}>
                        {(message) =>
                            <a href="#" className="coupon--make-bet__btn" onClick={this._onMakeBetClick}>{message}</a>}
                    </FormattedMessage>
                </div>
            </div>);
    }
}

function select(state) {
    const languageId = state.application.get('languageId');
    const user = state.auth.get('user');
    const bettingAvailable = state.application.get('bettingAvailable');
    const betSlips = state.application.getIn(['coupon', 'betSlips']);
    const betTypes = state.application.getIn(['coupon', 'betTypes']);
    const currentBetTypeIndex = state.application.getIn(['coupon', 'currentBetTypeIndex']);
    const amount = state.application.getIn(['coupon', 'amount']);
    const alternativeStake = state.application.getIn(['coupon', 'alternativeStake']);
    const freebet = state.application.getIn(['coupon', 'freebet']);
    const isFreebetObj = state.application.getIn(['coupon', 'isFreebet']);
    const bettingMode = state.application.getIn(['bettingMode']);
    const betHistory = state.application.getIn(['betHistory']);
    const isFreebet = isFreebetObj.get('isFreebet');
    const freebetAvailable = isFreebetObj.get('freebetAvailable');
    const nextKey = betHistory.size > 0 ? (parseInt(betHistory.keySeq().maxBy(x => parseInt(x))) + 1) : 1;
    const resourceName = resourceNames[user.get('resourceId')];
    const minBetAmount = MinAmountValues[resourceName];
    const maxWinAmount = MaxWinAmountValues[resourceName];

    return {
        countryCode: state.application.get('countryCode'),
        languageId,
        user,
        betSlips,
        betTypes,
        currentBetTypeIndex,
        amount,
        alternativeStake,
        freebet,
        isFreebet,
        freebetAvailable,
        bettingMode,
        nextKey,
        minBetAmount,
        maxWinAmount,
        bettingAvailable,
        resourceName
    };
}

export default connect(select, {
    makeBetAction, setCouponAmountAction, getBalanceAction, acceptRateValueChangeAction, requestBetTypesAction,
    toggleFreebetAction
})(Controls);



// WEBPACK FOOTER //
// ./js/sport-betting/components/bet-panel-control/coupon/Controls.jsx